import * as THREE from 'three';

export class HeroScene {
  constructor(container) {
    this.container = container;
    this.frameId = null;
    this.isDisposed = false;

    // Mouse tracking & isolated interaction states
    this.mouseX = -999;
    this.mouseY = -999;
    this.mouseNDC = new THREE.Vector2(-999, -999);
    this.hasPointerMoved = false;

    this.raycaster = new THREE.Raycaster();

    // Isolated per-model interaction states
    this.modelInteractions = {
      keyboard: {
        group: null,
        screenPos: new THREE.Vector2(),
        proximity: 0,
        targetProximity: 0,
        tiltX: 0,
        tiltY: 0,
        targetTiltX: 0,
        targetTiltY: 0,
        dragRotationX: 0,
        dragRotationY: 0,
        dragVelocityX: 0,
        dragVelocityY: 0,
        influenceRadius: 0.48, // Aspect-corrected screen distance threshold
      },
      headset: {
        group: null,
        screenPos: new THREE.Vector2(),
        proximity: 0,
        targetProximity: 0,
        tiltX: 0,
        tiltY: 0,
        targetTiltX: 0,
        targetTiltY: 0,
        dragRotationX: 0,
        dragRotationY: 0,
        dragVelocityX: 0,
        dragVelocityY: 0,
        influenceRadius: 0.48,
      },
      guitar: {
        group: null,
        screenPos: new THREE.Vector2(),
        proximity: 0,
        targetProximity: 0,
        tiltX: 0,
        tiltY: 0,
        targetTiltX: 0,
        targetTiltY: 0,
        dragRotationX: 0,
        dragRotationY: 0,
        dragVelocityX: 0,
        dragVelocityY: 0,
        influenceRadius: 0.48,
      },
    };

    this.activeHoveredModel = null;
    this.activeDraggedModel = null;
    this.isDragging = false;
    this.lastPointerX = 0;
    this.lastPointerY = 0;

    // Scroll tracking
    this.scrollY = 0;
    this.targetScroll = 0;
    this.scrollProgress = 0;

    this.init();
  }

  init() {
    const w = this.container.clientWidth || window.innerWidth;
    const h = this.container.clientHeight || window.innerHeight;

    // 1. Scene & Camera
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(38, w / h, 0.1, 1000);
    this.camera.position.set(0, 0, 19);

    // 2. High-Fidelity WebGL Renderer with ACES Tone Mapping
    this.renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance',
      stencil: false,
    });
    this.renderer.setSize(w, h);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.18;
    this.renderer.domElement.setAttribute('role', 'img');
    this.renderer.domElement.setAttribute('aria-label', 'Interactive 3D WebGL scene showcasing cybernetic mechanical macropad, cyber-minimalist studio headset, and electric guitar with physics-based mouse inertia');
    this.container.appendChild(this.renderer.domElement);

    // 3. Studio Lighting Rig
    this.setupLighting();

    // 4. Physically Based Materials Palette
    this.setupMaterials();

    // 5. Build High-Fidelity 3D Objects
    this.keyboardGroup = this.createMacropad();
    this.headsetGroup = this.createHeadset();
    this.guitarGroup = this.createWarlockGuitar();
    this.particlesGroup = this.createParticles();
    this.mistGroup = this.createMist();

    this.scene.add(this.keyboardGroup);
    this.scene.add(this.headsetGroup);
    this.scene.add(this.guitarGroup);
    this.scene.add(this.particlesGroup);
    this.scene.add(this.mistGroup);

    // Assign model groups to isolated interaction slots
    this.modelInteractions.keyboard.group = this.keyboardGroup;
    this.modelInteractions.headset.group = this.headsetGroup;
    this.modelInteractions.guitar.group = this.guitarGroup;

    // 6. Base Layout Configuration (20% reduced scales for balanced visual breathing room)
    this.basePositions = {
      keyboard: { x: 6.4, y: 0.1, z: 0.8, rx: 0.48, ry: -0.42, rz: 0.18, scale: 0.94 },
      headset: { x: -5.8, y: -4.4, z: 1.8, rx: 0.35, ry: -0.55, rz: 0.25, scale: 0.76 },
      guitar: { x: -6.6, y: 0.4, z: 0.2, rx: 0.28, ry: 0.42, rz: -0.36, scale: 0.84 },
    };

    this.adjustForScreenSize(w);

    // 7. Event Handlers
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);

    window.addEventListener('mousemove', this.onMouseMove, { passive: true });
    window.addEventListener('mousedown', this.onMouseDown, { passive: false });
    window.addEventListener('mouseup', this.onMouseUp, { passive: true });
    window.addEventListener('mouseleave', this.onMouseLeave, { passive: true });
    window.addEventListener('touchstart', this.onTouchStart, { passive: true });
    window.addEventListener('touchmove', this.onTouchMove, { passive: true });
    window.addEventListener('touchend', this.onTouchEnd, { passive: true });
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);

    // Initial scroll position
    this.scrollY = window.scrollY || window.pageYOffset || 0;
    this.targetScroll = this.scrollY;

    // 8. Animation Clock
    this.clock = new THREE.Clock();
    this.animate = this.animate.bind(this);
    this.animate();
  }

  setupLighting() {
    // 1. Soft Studio Ambient Foundation (Cool obsidian void base)
    const ambientLight = new THREE.AmbientLight(0x1e242c, 1.3);
    this.scene.add(ambientLight);

    // Hemispherical fill (cool sky cyan-slate + warm floor amber)
    const hemiLight = new THREE.HemisphereLight(0x2a3642, 0x151210, 1.1);
    this.scene.add(hemiLight);

    // 2. Cohesive Studio Key Lighting (Shared color temperature across all models)
    const mainKeyLight = new THREE.DirectionalLight(0xfaf8f5, 2.8);
    mainKeyLight.position.set(4, 16, 12);
    this.scene.add(mainKeyLight);

    // Balanced Left Directional Key (clean highlights for Guitar & Headset)
    const leftKeyLight = new THREE.DirectionalLight(0xf0eadc, 2.2);
    leftKeyLight.position.set(-12, 10, 8);
    this.scene.add(leftKeyLight);

    // Balanced Right Directional Key (clean highlights for Macropad)
    const rightKeyLight = new THREE.DirectionalLight(0xf5efe6, 2.4);
    rightKeyLight.position.set(12, 10, 8);
    this.scene.add(rightKeyLight);

    // 3. Shared Global Rim & Accent Lights
    // Back-left Electric Cyan rim (grazes guitar headstock & headset contour)
    const rimCyan = new THREE.DirectionalLight(0x38bdf8, 2.8);
    rimCyan.position.set(-16, -6, -6);
    this.scene.add(rimCyan);

    // Back-right Smoldering Burnt Ember fill (grazes macropad & lower frame)
    const fillEmber = new THREE.DirectionalLight(0xc85a32, 2.4);
    fillEmber.position.set(14, -10, -4);
    this.scene.add(fillEmber);

    // 4. Dedicated Model Accent Illumination (Matched local aura for each hardware piece)
    // Guitar: Ember Edge Glow for the Warlock bevels
    const warlockGlow = new THREE.PointLight(0xe06d44, 3.2, 14);
    warlockGlow.position.set(-6.5, 0.5, 3.2);
    this.scene.add(warlockGlow);

    // Headset: Electric Cyan Glow for Neon Ring & metallic sliders
    const headsetCyanGlow = new THREE.PointLight(0x22d3ee, 3.2, 12);
    headsetCyanGlow.position.set(-5.8, -4.2, 3.0);
    this.scene.add(headsetCyanGlow);

    // Macropad: Dedicated Alabaster & Burnt Ember Point Lights (matching the other models)
    const macropadGlow = new THREE.PointLight(0xfaf5ec, 2.8, 12);
    macropadGlow.position.set(6.4, 0.5, 3.0);
    this.scene.add(macropadGlow);

    const macropadEmberGlow = new THREE.PointLight(0xe06d44, 2.2, 10);
    macropadEmberGlow.position.set(7.6, -1.0, 2.4);
    this.scene.add(macropadEmberGlow);

    // Overhead studio highlight for crisp top bevel reflection
    const topLight = new THREE.DirectionalLight(0xffffff, 1.3);
    topLight.position.set(0, 22, 0);
    this.scene.add(topLight);
  }

  setupMaterials() {
    this.mat = {
      // Tactile Sand Sheet Case (matching DESIGN.md #EDE8DC)
      sandCase: new THREE.MeshStandardMaterial({
        color: 0xede8dc,
        metalness: 0.05,
        roughness: 0.42,
      }),
      // Recessed Obsidian Plate (#0E100F)
      obsidianPlate: new THREE.MeshStandardMaterial({
        color: 0x121415,
        metalness: 0.6,
        roughness: 0.35,
      }),
      // Polished Brass / Metallic Accent
      brass: new THREE.MeshStandardMaterial({
        color: 0xd4af37,
        metalness: 0.95,
        roughness: 0.18,
      }),
      // Chrome Hardware & USB Plug
      chrome: new THREE.MeshStandardMaterial({
        color: 0xe0e5ea,
        metalness: 0.98,
        roughness: 0.12,
      }),
      // Stainless Steel
      steel: new THREE.MeshStandardMaterial({
        color: 0x94a3b8,
        metalness: 0.9,
        roughness: 0.35,
      }),
      // Burnt Ember Keycap Body (#C85A32)
      keyEmberBody: new THREE.MeshStandardMaterial({
        color: 0xc85a32,
        metalness: 0.1,
        roughness: 0.45,
      }),
      // Alabaster Cream Keycap Body (#F0EADC / #FAF8F5)
      keyCreamBody: new THREE.MeshStandardMaterial({
        color: 0xf5efe4,
        metalness: 0.05,
        roughness: 0.45,
      }),
      // Rubber Cable Jacket & Strain Relief
      cableRubber: new THREE.MeshStandardMaterial({
        color: 0xe8e2d4,
        metalness: 0.05,
        roughness: 0.6,
      }),
      // Dark Grommet Collar
      darkGrommet: new THREE.MeshStandardMaterial({
        color: 0x222426,
        metalness: 0.2,
        roughness: 0.7,
      }),
      // Headphone Cushion
      cushion: new THREE.MeshStandardMaterial({
        color: 0x121314,
        metalness: 0.05,
        roughness: 0.8,
      }),

      // --- WARLOCK GUITAR SPECIFIC PBR MATERIALS ---
      // Matte Obsidian Warlock Body Face (#0E100F)
      warlockBodyFace: new THREE.MeshStandardMaterial({
        color: 0x121316,
        metalness: 0.25,
        roughness: 0.45,
      }),
      // Glowing Burnt Ember Bevel Rim (#E06D44 / #C85A32)
      warlockBevelEmber: new THREE.MeshStandardMaterial({
        color: 0xe06d44,
        emissive: 0x8a2008,
        emissiveIntensity: 0.75,
        metalness: 0.3,
        roughness: 0.25,
      }),
      // Fretboard (Satin Ebony / Dark Obsidian)
      warlockFretboard: new THREE.MeshStandardMaterial({
        color: 0x161719,
        metalness: 0.1,
        roughness: 0.5,
      }),
      // Burnt Ember Fret Inlays
      warlockInlayEmber: new THREE.MeshStandardMaterial({
        color: 0xe06d44,
        emissive: 0x992408,
        emissiveIntensity: 0.85,
        metalness: 0.2,
        roughness: 0.3,
      }),
      // Black Nickel Hardware (pickups, bridge, knobs)
      blackNickel: new THREE.MeshStandardMaterial({
        color: 0x22252a,
        metalness: 0.9,
        roughness: 0.2,
      }),

      // --- CYBER-MINIMALIST WIRELESS HEADSET MATERIALS ---
      // Satin Obsidian Headband & Outer Cup Shell (#14161A)
      headsetObsidianShell: new THREE.MeshStandardMaterial({
        color: 0x14161a,
        metalness: 0.22,
        roughness: 0.32,
      }),
      // Sleek Gloss Black Pivot Caps & Trim Rings (#181A1F)
      headsetGlossTrim: new THREE.MeshStandardMaterial({
        color: 0x181a1f,
        metalness: 0.45,
        roughness: 0.16,
      }),
      // Radiant Neon Cyan Earcup Light Ring (#22D3EE / #00F0FF)
      headsetNeonCyanRing: new THREE.MeshStandardMaterial({
        color: 0x22d3ee,
        emissive: 0x22d3ee,
        emissiveIntensity: 3.0,
        metalness: 0.1,
        roughness: 0.1,
      }),
      // Matte Slate Charcoal Memory Foam Ear Cushions (#282A30)
      headsetCushionCharcoal: new THREE.MeshStandardMaterial({
        color: 0x282a30,
        metalness: 0.04,
        roughness: 0.88,
      }),
      // Recessed Acoustic Driver Cloth Grille (#151619)
      headsetDriverCloth: new THREE.MeshStandardMaterial({
        color: 0x151619,
        metalness: 0.08,
        roughness: 0.78,
      }),
      // Soft Inner Silicone Headband Cushion Pad (#101114)
      headsetBandPad: new THREE.MeshStandardMaterial({
        color: 0x101114,
        metalness: 0.05,
        roughness: 0.65,
      }),

      // Glowing Cyan LED / Indicator
      cyanGlow: new THREE.MeshBasicMaterial({
        color: 0x38bdf8,
      }),
    };
  }

  // Generate crisp canvas texture for keycap legends
  createKeycapTexture(bgColor, textColor, char, isWide = false) {
    const canvas = document.createElement('canvas');
    canvas.width = isWide ? 512 : 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');

    // Background color
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Subtle dished keycap inner bevel highlight
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const grad = ctx.createRadialGradient(cx, cy, 30, cx, cy, canvas.width * 0.45);
    grad.addColorStop(0, 'rgba(255, 255, 255, 0.12)');
    grad.addColorStop(1, 'rgba(0, 0, 0, 0.15)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Crisp inner keycap border
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.22)';
    ctx.lineWidth = 8;
    ctx.strokeRect(6, 6, canvas.width - 12, canvas.height - 12);

    // Bold typography (Space Grotesk style)
    ctx.fillStyle = textColor;
    ctx.font = isWide ? '900 130px "Space Grotesk", sans-serif' : '900 116px "Space Grotesk", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(char, cx, cy + 4);

    const tex = new THREE.CanvasTexture(canvas);
    tex.anisotropy = 8;
    return tex;
  }

  // Custom mechanical WASD macropad
  createMacropad() {
    const group = new THREE.Group();

    const caseWidth = 3.6;
    const caseDepth = 3.8;
    const caseHeight = 1.0;

    // Main wedge chassis
    const baseGeo = new THREE.BoxGeometry(caseWidth, caseHeight, caseDepth);
    const baseMesh = new THREE.Mesh(baseGeo, this.mat.sandCase);
    baseMesh.position.y = -0.35;
    baseMesh.rotation.x = -0.16;
    group.add(baseMesh);

    // Beveled bottom bezel lip
    const bezelGeo = new THREE.BoxGeometry(caseWidth + 0.2, 0.22, caseDepth + 0.2);
    const bezelMesh = new THREE.Mesh(bezelGeo, this.mat.sandCase);
    bezelMesh.position.set(0, -0.65, 0);
    bezelMesh.rotation.x = -0.16;
    group.add(bezelMesh);

    // Recessed Switch Plate (Dark Obsidian #121415)
    const plateGeo = new THREE.BoxGeometry(3.0, 0.1, 3.2);
    const plateMesh = new THREE.Mesh(plateGeo, this.mat.obsidianPlate);
    plateMesh.position.set(0, 0.16, -0.05);
    plateMesh.rotation.x = -0.16;
    group.add(plateMesh);

    // Keycaps with dynamic legends
    const texW = this.createKeycapTexture('#C85A32', '#FAF8F5', 'W');
    const texA = this.createKeycapTexture('#C85A32', '#FAF8F5', 'A');
    const texS = this.createKeycapTexture('#C85A32', '#FAF8F5', 'S');
    const texD = this.createKeycapTexture('#C85A32', '#FAF8F5', 'D');
    const texQ = this.createKeycapTexture('#F5EFE4', '#121415', 'Q');
    const texE = this.createKeycapTexture('#F5EFE4', '#121415', 'E');
    const texSpace = this.createKeycapTexture('#F5EFE4', '#121415', '—', true);

    const makeKeycap = (width, depth, height, topTex, bodyMat) => {
      const topMat = new THREE.MeshStandardMaterial({
        map: topTex,
        metalness: 0.08,
        roughness: 0.45,
      });
      const mats = [bodyMat, bodyMat, topMat, bodyMat, bodyMat, bodyMat];
      const geo = new THREE.BoxGeometry(width, height, depth);
      return new THREE.Mesh(geo, mats);
    };

    const keyH = 0.38;
    const kw = 0.74;
    const kd = 0.74;
    const stepX = 0.86;
    const stepZ = 0.86;

    const keyMount = new THREE.Group();
    keyMount.position.set(0, 0.22, -0.05);
    keyMount.rotation.x = -0.16;

    // Row 1: Q, W, E
    const keyQ = makeKeycap(kw, kd, keyH, texQ, this.mat.keyCreamBody);
    keyQ.position.set(-stepX, keyH / 2, -stepZ);
    keyMount.add(keyQ);

    const keyW = makeKeycap(kw, kd, keyH, texW, this.mat.keyEmberBody);
    keyW.position.set(0, keyH / 2, -stepZ);
    keyMount.add(keyW);

    const keyE = makeKeycap(kw, kd, keyH, texE, this.mat.keyCreamBody);
    keyE.position.set(stepX, keyH / 2, -stepZ);
    keyMount.add(keyE);

    // Row 2: A, S, D
    const keyA = makeKeycap(kw, kd, keyH, texA, this.mat.keyEmberBody);
    keyA.position.set(-stepX, keyH / 2, 0);
    keyMount.add(keyA);

    const keyS = makeKeycap(kw, kd, keyH, texS, this.mat.keyEmberBody);
    keyS.position.set(0, keyH / 2, 0);
    keyMount.add(keyS);

    const keyD = makeKeycap(kw, kd, keyH, texD, this.mat.keyEmberBody);
    keyD.position.set(stepX, keyH / 2, 0);
    keyMount.add(keyD);

    // Row 3: Spacebar / Dash key
    const keySpace = makeKeycap(kw + stepX, kd, keyH, texSpace, this.mat.keyCreamBody);
    keySpace.position.set(stepX / 2, keyH / 2, stepZ);
    keyMount.add(keySpace);

    group.add(keyMount);

    // Front Rotary Knob
    const knobGroup = new THREE.Group();
    knobGroup.position.set(-0.95, -0.28, 1.72);
    knobGroup.rotation.x = 0.16;

    const knobBaseGeo = new THREE.CylinderGeometry(0.38, 0.42, 0.32, 28);
    knobBaseGeo.rotateX(Math.PI / 2);
    const knobBase = new THREE.Mesh(knobBaseGeo, this.mat.sandCase);
    knobGroup.add(knobBase);

    const knobFaceGeo = new THREE.CylinderGeometry(0.34, 0.34, 0.05, 28);
    knobFaceGeo.rotateX(Math.PI / 2);
    const knobFace = new THREE.Mesh(knobFaceGeo, this.mat.brass);
    knobFace.position.z = 0.18;
    knobGroup.add(knobFace);

    group.add(knobGroup);

    // Curved USB Cable & Plug
    const cableGroup = new THREE.Group();
    const cablePoints = [
      new THREE.Vector3(-1.4, 0.05, -1.6),
      new THREE.Vector3(-2.1, 0.25, -2.0),
      new THREE.Vector3(-3.0, 0.35, -1.7),
      new THREE.Vector3(-3.8, 0.2, -0.8),
      new THREE.Vector3(-4.4, 0.05, 0.2),
    ];
    const cableCurve = new THREE.CatmullRomCurve3(cablePoints);
    const cableGeo = new THREE.TubeGeometry(cableCurve, 36, 0.08, 12, false);
    const cableMesh = new THREE.Mesh(cableGeo, this.mat.cableRubber);
    cableGroup.add(cableMesh);

    const grommetGeo = new THREE.CylinderGeometry(0.12, 0.15, 0.28, 14);
    grommetGeo.rotateZ(Math.PI / 4);
    const grommet = new THREE.Mesh(grommetGeo, this.mat.darkGrommet);
    grommet.position.set(-1.45, 0.06, -1.62);
    cableGroup.add(grommet);

    const usbHousingGeo = new THREE.BoxGeometry(0.38, 0.24, 0.65);
    const usbHousing = new THREE.Mesh(usbHousingGeo, this.mat.sandCase);
    usbHousing.position.set(-4.4, 0.05, 0.2);
    usbHousing.rotation.y = 0.55;
    cableGroup.add(usbHousing);

    const usbShieldGeo = new THREE.BoxGeometry(0.3, 0.16, 0.35);
    const usbShield = new THREE.Mesh(usbShieldGeo, this.mat.chrome);
    usbShield.position.set(-4.62, 0.05, 0.52);
    usbShield.rotation.y = 0.55;
    cableGroup.add(usbShield);

    const usbTongueGeo = new THREE.BoxGeometry(0.24, 0.04, 0.28);
    const usbTongue = new THREE.Mesh(usbTongueGeo, this.mat.brass);
    usbTongue.position.set(-4.62, 0.07, 0.52);
    usbTongue.rotation.y = 0.55;
    cableGroup.add(usbTongue);

    group.add(cableGroup);

    return group;
  }

  // --- NEW: B.C. RICH WARLOCK CYBER-EDITORIAL GUITAR ---
  createWarlockGuitar() {
    const group = new THREE.Group();

    // 1. Precise Razor-Sharp Warlock Body Contour Shape
    const bodyShape = new THREE.Shape();
    // Start at neck pocket
    bodyShape.moveTo(-0.28, 0.95);
    bodyShape.lineTo(0.28, 0.95);

    // Upper-Right Horn (pointed cutaway prong)
    bodyShape.lineTo(0.42, 0.82);
    bodyShape.lineTo(1.15, 1.45); // Upper right prong tip
    bodyShape.lineTo(0.78, 0.58);
    bodyShape.lineTo(1.35, -0.22); // Middle-right horn tip
    bodyShape.lineTo(0.68, -0.65);

    // Lower-Right Tail Flare
    bodyShape.lineTo(0.88, -1.65); // Lower right prong
    bodyShape.lineTo(0.32, -1.35);

    // Bottom Inward V-Notch
    bodyShape.lineTo(0.0, -1.95); // Deep bottom V-crotch

    // Lower-Left Long Extended Wing
    bodyShape.lineTo(-1.1, -2.65); // Long lower-left razor point
    bodyShape.lineTo(-0.68, -1.15);

    // Main Left Wing Flare
    bodyShape.lineTo(-1.85, -0.65); // Outer left spike
    bodyShape.lineTo(-1.32, 0.15);
    bodyShape.lineTo(-1.75, 0.92); // Upper-left main wing spike
    bodyShape.lineTo(-0.45, 0.88);
    bodyShape.lineTo(-0.28, 0.95);

    // Extrude with Bevel: Face material [0] is Obsidian, Sides & Bevels [1] are Glowing Burnt Ember!
    const extrudeSettings = {
      depth: 0.24,
      bevelEnabled: true,
      bevelSegments: 4,
      steps: 1,
      bevelSize: 0.08,
      bevelThickness: 0.08,
    };

    const bodyGeo = new THREE.ExtrudeGeometry(bodyShape, extrudeSettings);
    // Center geometry around Z
    bodyGeo.center();

    // Materials array: index 0 = front & back flat faces, index 1 = extruded sides and beveled glowing rim
    const bodyMaterials = [
      this.mat.warlockBodyFace,   // Matte Obsidian Black face
      this.mat.warlockBevelEmber, // Radiant Glowing Burnt Ember Bevel
    ];

    const bodyMesh = new THREE.Mesh(bodyGeo, bodyMaterials);
    // Orient upright on Z plane
    bodyMesh.rotation.z = Math.PI;
    bodyMesh.rotation.y = Math.PI;
    group.add(bodyMesh);

    // 2. Dual Open-Coil Humbucker Pickups (Black bobbins with nickel pole screws)
    [-0.32, -0.88].forEach((py) => {
      const pickupGroup = new THREE.Group();
      pickupGroup.position.set(0, py, 0.18);

      // Pickup mounting ring (black nickel)
      const ringGeo = new THREE.BoxGeometry(0.88, 0.42, 0.1);
      const ring = new THREE.Mesh(ringGeo, this.mat.blackNickel);
      pickupGroup.add(ring);

      // Dual coil bobbins
      [-0.09, 0.09].forEach((by) => {
        const bobbinGeo = new THREE.BoxGeometry(0.8, 0.15, 0.08);
        const bobbin = new THREE.Mesh(bobbinGeo, this.mat.warlockBodyFace);
        bobbin.position.set(0, by, 0.06);
        pickupGroup.add(bobbin);

        // 6 Nickel pole pieces per bobbin
        for (let p = -0.28; p <= 0.28; p += 0.112) {
          const poleGeo = new THREE.CylinderGeometry(0.024, 0.024, 0.04, 8);
          poleGeo.rotateX(Math.PI / 2);
          const pole = new THREE.Mesh(poleGeo, this.mat.chrome);
          pole.position.set(p, by, 0.1);
          pickupGroup.add(pole);
        }
      });

      group.add(pickupGroup);
    });

    // 3. Black Nickel Tune-O-Matic Bridge & Tailpiece
    const bridgeGeo = new THREE.BoxGeometry(0.86, 0.14, 0.16);
    const bridge = new THREE.Mesh(bridgeGeo, this.mat.blackNickel);
    bridge.position.set(0, -1.35, 0.22);
    group.add(bridge);

    const tailpieceGeo = new THREE.BoxGeometry(0.78, 0.09, 0.14);
    const tailpiece = new THREE.Mesh(tailpieceGeo, this.mat.blackNickel);
    tailpiece.position.set(0, -1.62, 0.2);
    group.add(tailpiece);

    // 4. Slanted Knurled Control Knobs on Lower Wing
    [
      { x: 0.38, y: -1.25 },
      { x: 0.52, y: -1.52 },
      { x: 0.65, y: -1.78 },
    ].forEach((pos) => {
      const knobGeo = new THREE.CylinderGeometry(0.1, 0.1, 0.14, 16);
      knobGeo.rotateX(Math.PI / 2);
      const knob = new THREE.Mesh(knobGeo, this.mat.blackNickel);
      knob.position.set(pos.x, pos.y, 0.22);
      group.add(knob);
    });

    // 5. Long Black Neck & Fretboard
    const neckLength = 3.6;
    const neckGeo = new THREE.BoxGeometry(0.36, neckLength, 0.16);
    const neck = new THREE.Mesh(neckGeo, this.mat.warlockBodyFace);
    neck.position.set(0, 1.85, 0.08);
    group.add(neck);

    const fretboardGeo = new THREE.BoxGeometry(0.38, neckLength, 0.05);
    const fretboard = new THREE.Mesh(fretboardGeo, this.mat.warlockFretboard);
    fretboard.position.set(0, 1.85, 0.18);
    group.add(fretboard);

    // 24 Frets with Custom Burnt Ember Tribal Inlays
    const inlayFrets = [1, 3, 5, 7, 9, 12, 15, 17, 19, 21, 24];
    for (let f = 0; f < 24; f++) {
      const fy = 0.15 + f * 0.142;
      const fretGeo = new THREE.CylinderGeometry(0.009, 0.009, 0.36, 6);
      fretGeo.rotateZ(Math.PI / 2);
      const fret = new THREE.Mesh(fretGeo, this.mat.steel);
      fret.position.set(0, fy, 0.21);
      group.add(fret);

      // Custom Winged / Tribal Inlays matching reference image
      if (inlayFrets.includes(f + 1)) {
        const isDouble = f + 1 === 12;
        const inlayWidth = isDouble ? 0.26 : 0.18;
        const inlayGeo = new THREE.BoxGeometry(inlayWidth, 0.045, 0.015);
        const inlay = new THREE.Mesh(inlayGeo, this.mat.warlockInlayEmber);
        inlay.position.set(0, fy - 0.07, 0.21);
        group.add(inlay);
      }
    }

    // 6. Iconic B.C. Rich Reverse Widow Spiked Headstock
    const headShape = new THREE.Shape();
    headShape.moveTo(-0.2, 0);
    headShape.lineTo(0.2, 0);
    // Right flare & horn
    headShape.lineTo(0.38, 0.65);
    headShape.lineTo(0.55, 1.25); // Sharp right horn tip
    headShape.lineTo(0.28, 0.95);
    // Center notch
    headShape.lineTo(0.0, 0.72);
    // Left flare & horn
    headShape.lineTo(-0.28, 0.95);
    headShape.lineTo(-0.55, 1.25); // Sharp left horn tip
    headShape.lineTo(-0.38, 0.65);
    headShape.lineTo(-0.2, 0);

    const headExtrudeSettings = {
      depth: 0.14,
      bevelEnabled: true,
      bevelSegments: 3,
      steps: 1,
      bevelSize: 0.04,
      bevelThickness: 0.04,
    };

    const headGeo = new THREE.ExtrudeGeometry(headShape, headExtrudeSettings);
    headGeo.center();

    const headMaterials = [
      this.mat.warlockBodyFace,   // Black face
      this.mat.warlockBevelEmber, // Radiant Ember beveled edge
    ];
    const headMesh = new THREE.Mesh(headGeo, headMaterials);
    headMesh.position.set(0, 4.15, 0.14);
    headMesh.rotation.z = Math.PI;
    headMesh.rotation.y = Math.PI;
    headMesh.rotation.x = 0.12;
    group.add(headMesh);

    // 3x3 Black Chrome Tuning Pegs
    [-0.34, 0.34].forEach((px) => {
      for (let i = 0; i < 3; i++) {
        const pegPostGeo = new THREE.CylinderGeometry(0.028, 0.028, 0.2, 8);
        pegPostGeo.rotateZ(Math.PI / 2);
        const pegPost = new THREE.Mesh(pegPostGeo, this.mat.blackNickel);
        pegPost.position.set(px, 3.85 + i * 0.25, 0.18);
        group.add(pegPost);

        const keyGeo = new THREE.BoxGeometry(0.035, 0.14, 0.07);
        const key = new THREE.Mesh(keyGeo, this.mat.blackNickel);
        key.position.set(px + (px > 0 ? 0.11 : -0.11), 3.85 + i * 0.25, 0.18);
        group.add(key);
      }
    });

    // Red Script Emblem on Headstock
    const emblemGeo = new THREE.BoxGeometry(0.18, 0.06, 0.02);
    const emblem = new THREE.Mesh(emblemGeo, this.mat.warlockInlayEmber);
    emblem.position.set(0, 4.05, 0.22);
    group.add(emblem);

    // 7. 6 Steel Strings Spanning from Bridge to Headstock
    for (let s = -0.13; s <= 0.13; s += 0.052) {
      const gauge = 0.008 + Math.abs(s + 0.13) * 0.016;
      const stringGeo = new THREE.CylinderGeometry(gauge, gauge, 5.3, 6);
      const stringMesh = new THREE.Mesh(stringGeo, this.mat.steel);
      stringMesh.position.set(s, 1.25, 0.23);
      group.add(stringMesh);
    }

    return group;
  }

  // --- CYBER-MINIMALIST WIRELESS HEADSET (REFERENCE TRANSFORMATION) ---
  createHeadset() {
    const group = new THREE.Group();

    // 1. Sleek Continuous Arch Headband
    const bandPoints = [
      new THREE.Vector3(-1.72, -0.65, 0),
      new THREE.Vector3(-1.76, 0.45, 0),
      new THREE.Vector3(-1.42, 1.4, 0),
      new THREE.Vector3(0, 1.95, 0),
      new THREE.Vector3(1.42, 1.4, 0),
      new THREE.Vector3(1.76, 0.45, 0),
      new THREE.Vector3(1.72, -0.65, 0),
    ];
    const headbandPath = new THREE.CatmullRomCurve3(bandPoints);

    // Rounded rectangle cross-section (sleek flat ribbon with filleted edges)
    const bandShape = new THREE.Shape();
    const bw = 0.46, bh = 0.075, br = 0.024;
    bandShape.moveTo(-bw / 2 + br, -bh / 2);
    bandShape.lineTo(bw / 2 - br, -bh / 2);
    bandShape.quadraticCurveTo(bw / 2, -bh / 2, bw / 2, -bh / 2 + br);
    bandShape.lineTo(bw / 2, bh / 2 - br);
    bandShape.quadraticCurveTo(bw / 2, bh / 2, bw / 2 - br, bh / 2);
    bandShape.lineTo(-bw / 2 + br, bh / 2);
    bandShape.quadraticCurveTo(-bw / 2, bh / 2, -bw / 2, bh / 2 - br);
    bandShape.lineTo(-bw / 2, -bh / 2 + br);
    bandShape.quadraticCurveTo(-bw / 2, -bh / 2, -bw / 2 + br, -bh / 2);

    const headbandGeo = new THREE.ExtrudeGeometry(bandShape, {
      extrudePath: headbandPath,
      steps: 64,
      bevelEnabled: false,
    });
    const headband = new THREE.Mesh(headbandGeo, this.mat.headsetObsidianShell);
    group.add(headband);

    // 2. Soft Inner Silicone Headband Comfort Strip (Underside of top arch)
    const padPoints = [
      new THREE.Vector3(-1.18, 1.05, 0),
      new THREE.Vector3(-0.7, 1.5, 0),
      new THREE.Vector3(0, 1.86, 0),
      new THREE.Vector3(0.7, 1.5, 0),
      new THREE.Vector3(1.18, 1.05, 0),
    ];
    const padPath = new THREE.CatmullRomCurve3(padPoints);
    const padShape = new THREE.Shape();
    const pw = 0.38, ph = 0.048, pr = 0.015;
    padShape.moveTo(-pw / 2 + pr, -ph / 2);
    padShape.lineTo(pw / 2 - pr, -ph / 2);
    padShape.quadraticCurveTo(pw / 2, -ph / 2, pw / 2, -ph / 2 + pr);
    padShape.lineTo(pw / 2, ph / 2 - pr);
    padShape.quadraticCurveTo(pw / 2, ph / 2, pw / 2 - pr, ph / 2);
    padShape.lineTo(-pw / 2 + pr, ph / 2);
    padShape.quadraticCurveTo(-pw / 2, ph / 2, -pw / 2, ph / 2 - pr);
    padShape.lineTo(-pw / 2, -ph / 2 + pr);
    padShape.quadraticCurveTo(-pw / 2, -ph / 2, -pw / 2 + pr, -ph / 2);

    const padGeo = new THREE.ExtrudeGeometry(padShape, {
      extrudePath: padPath,
      steps: 40,
      bevelEnabled: false,
    });
    const pad = new THREE.Mesh(padGeo, this.mat.headsetBandPad);
    group.add(pad);

    // 3. Left & Right Earcup Assembly
    [-1.72, 1.72].forEach((xSide) => {
      const isRight = xSide > 0;
      const dir = isRight ? -1 : 1; // +dir = inwards towards head, -dir = outwards
      const cupGroup = new THREE.Group();
      cupGroup.position.set(xSide, -0.68, 0);

      // Ergonomic inward head cant
      cupGroup.rotation.z = isRight ? -0.12 : 0.12;
      cupGroup.rotation.y = isRight ? 0.08 : -0.08;

      // --- Outer Stepped Pivot Boss / Cap ---
      // Outer beveled ring
      const outerCapGeo = new THREE.CylinderGeometry(0.52, 0.56, 0.14, 36);
      outerCapGeo.rotateZ(Math.PI / 2);
      const outerCap = new THREE.Mesh(outerCapGeo, this.mat.headsetObsidianShell);
      outerCap.position.x = -dir * 0.18;
      cupGroup.add(outerCap);

      // Mid gloss trim ring
      const midRingGeo = new THREE.CylinderGeometry(0.42, 0.44, 0.08, 36);
      midRingGeo.rotateZ(Math.PI / 2);
      const midRing = new THREE.Mesh(midRingGeo, this.mat.headsetGlossTrim);
      midRing.position.x = -dir * 0.24;
      cupGroup.add(midRing);

      // Center recessed disc
      const centerDiscGeo = new THREE.CylinderGeometry(0.32, 0.32, 0.05, 36);
      centerDiscGeo.rotateZ(Math.PI / 2);
      const centerDisc = new THREE.Mesh(centerDiscGeo, this.mat.headsetObsidianShell);
      centerDisc.position.x = -dir * 0.27;
      cupGroup.add(centerDisc);

      // --- Main Tapered Earcup Housing Shell ---
      const shellGeo = new THREE.CylinderGeometry(0.85, 0.54, 0.38, 40);
      shellGeo.rotateZ(isRight ? -Math.PI / 2 : Math.PI / 2);
      const shell = new THREE.Mesh(shellGeo, this.mat.headsetObsidianShell);
      shell.position.x = dir * 0.06;
      cupGroup.add(shell);

      // Shell beveled transition collar (polished gloss black)
      const collarGeo = new THREE.CylinderGeometry(0.87, 0.85, 0.06, 40);
      collarGeo.rotateZ(Math.PI / 2);
      const collar = new THREE.Mesh(collarGeo, this.mat.headsetGlossTrim);
      collar.position.x = dir * 0.24;
      cupGroup.add(collar);

      // --- Radiant Neon Cyan Earcup Light Ring (#22D3EE / #00F0FF) ---
      const cyanRingGeo = new THREE.TorusGeometry(0.865, 0.026, 16, 48);
      cyanRingGeo.rotateY(Math.PI / 2);
      const cyanRing = new THREE.Mesh(cyanRingGeo, this.mat.headsetNeonCyanRing);
      cyanRing.position.x = dir * 0.28;
      cupGroup.add(cyanRing);

      const cyanStripGeo = new THREE.CylinderGeometry(0.86, 0.86, 0.038, 40, 1, true);
      cyanStripGeo.rotateZ(Math.PI / 2);
      const cyanStrip = new THREE.Mesh(cyanStripGeo, this.mat.headsetNeonCyanRing);
      cyanStrip.position.x = dir * 0.28;
      cupGroup.add(cyanStrip);

      // --- Plush Memory-Foam Ear Cushion ---
      const cushionGeo = new THREE.TorusGeometry(0.65, 0.24, 24, 48);
      cushionGeo.rotateY(Math.PI / 2);
      const cushion = new THREE.Mesh(cushionGeo, this.mat.headsetCushionCharcoal);
      cushion.position.x = dir * 0.44;
      cushion.scale.set(1.0, 1.06, 0.96);
      cupGroup.add(cushion);

      // --- Acoustic Speaker Cloth Grille & Driver Dome ---
      const backingGeo = new THREE.CylinderGeometry(0.52, 0.52, 0.04, 32);
      backingGeo.rotateZ(Math.PI / 2);
      const backing = new THREE.Mesh(backingGeo, this.mat.headsetDriverCloth);
      backing.position.x = dir * 0.38;
      cupGroup.add(backing);

      const domeGeo = new THREE.SphereGeometry(0.18, 18, 10, 0, Math.PI * 2, 0, Math.PI / 2);
      domeGeo.rotateZ(isRight ? Math.PI / 2 : -Math.PI / 2);
      const dome = new THREE.Mesh(domeGeo, this.mat.headsetObsidianShell);
      dome.position.x = dir * 0.40;
      cupGroup.add(dome);

      group.add(cupGroup);
    });

    return group;
  }

  createParticles() {
    const count = 110;
    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 32;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 22;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 16;
    }

    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.PointsMaterial({
      color: 0x38bdf8,
      size: 0.09,
      transparent: true,
      opacity: 0.65,
    });

    return new THREE.Points(geo, mat);
  }

  createMistTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');

    // Multi-stop soft organic radial plume
    const grad = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    grad.addColorStop(0.0, 'rgba(255, 255, 255, 0.95)');
    grad.addColorStop(0.2, 'rgba(235, 245, 255, 0.65)');
    grad.addColorStop(0.45, 'rgba(210, 230, 250, 0.28)');
    grad.addColorStop(0.7, 'rgba(180, 205, 230, 0.08)');
    grad.addColorStop(1.0, 'rgba(0, 0, 0, 0)');

    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 256, 256);

    // Add subtle soft secondary lobes for wispy organic look
    const lobes = [
      { x: 105, y: 110, r: 75, a: 0.18 },
      { x: 145, y: 115, r: 80, a: 0.15 },
      { x: 120, y: 145, r: 70, a: 0.16 },
      { x: 110, y: 135, r: 60, a: 0.14 },
    ];
    for (const lobe of lobes) {
      const lobeGrad = ctx.createRadialGradient(lobe.x, lobe.y, 0, lobe.x, lobe.y, lobe.r);
      lobeGrad.addColorStop(0.0, `rgba(255, 255, 255, ${lobe.a})`);
      lobeGrad.addColorStop(0.5, `rgba(220, 235, 255, ${lobe.a * 0.4})`);
      lobeGrad.addColorStop(1.0, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = lobeGrad;
      ctx.beginPath();
      ctx.arc(lobe.x, lobe.y, lobe.r, 0, Math.PI * 2);
      ctx.fill();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
    return texture;
  }

  createMist() {
    const mistGroup = new THREE.Group();
    const mistTexture = this.createMistTexture();
    this.mistTexture = mistTexture;

    const geo = new THREE.PlaneGeometry(1, 1);
    this.mistGeo = geo;
    this.mistPuffs = [];

    // --- LEFT FLANK MIST (In front of Guitar & Headset: strictly x <= -4.8, keeping central text clear) ---
    const leftPuffConfigs = [
      // In front of Warlock Guitar (z: 2.4 to 3.8, guitar is at z: 0.2)
      { x: -6.4, y: 1.2, z: 2.8, scale: 5.5, color: 0x38bdf8, opacity: 0.075, speedX: 0.35, speedY: 0.45, rotSpeed: 0.015, phase: 0.0 },
      { x: -7.6, y: -0.2, z: 3.4, scale: 6.2, color: 0x22d3ee, opacity: 0.065, speedX: 0.42, speedY: 0.32, rotSpeed: -0.012, phase: 1.4 },
      { x: -5.4, y: -0.8, z: 2.4, scale: 4.8, color: 0xe06d44, opacity: 0.055, speedX: 0.28, speedY: 0.38, rotSpeed: 0.018, phase: 2.8 },
      { x: -7.8, y: 2.2, z: 3.8, scale: 5.8, color: 0x38bdf8, opacity: 0.060, speedX: 0.38, speedY: 0.25, rotSpeed: -0.014, phase: 4.1 },

      // In front of Studio Headset (z: 2.6 to 4.2, headset is at z: 1.8)
      { x: -5.6, y: -3.8, z: 3.2, scale: 5.2, color: 0x22d3ee, opacity: 0.080, speedX: 0.32, speedY: 0.40, rotSpeed: 0.012, phase: 0.8 },
      { x: -6.8, y: -5.0, z: 3.9, scale: 5.6, color: 0x38bdf8, opacity: 0.070, speedX: 0.45, speedY: 0.35, rotSpeed: -0.016, phase: 2.3 },
      { x: -4.8, y: -4.2, z: 2.6, scale: 4.4, color: 0x88ddff, opacity: 0.065, speedX: 0.30, speedY: 0.48, rotSpeed: 0.020, phase: 3.7 },
      { x: -7.2, y: -3.2, z: 4.2, scale: 6.0, color: 0x1e3a50, opacity: 0.090, speedX: 0.25, speedY: 0.28, rotSpeed: -0.010, phase: 5.2 },
    ];

    // --- RIGHT FLANK MIST (In front of WASD Macropad: strictly x >= +5.0, keeping central text clear) ---
    const rightPuffConfigs = [
      // In front of Macropad (z: 2.4 to 4.0, macropad is at z: 0.8)
      { x: 6.2, y: 0.8, z: 2.9, scale: 5.4, color: 0xf5eadc, opacity: 0.070, speedX: 0.34, speedY: 0.42, rotSpeed: -0.015, phase: 0.5 },
      { x: 7.4, y: -0.4, z: 3.5, scale: 6.0, color: 0xe08a55, opacity: 0.060, speedX: 0.40, speedY: 0.30, rotSpeed: 0.014, phase: 1.9 },
      { x: 5.2, y: -0.9, z: 2.4, scale: 4.6, color: 0xfaf5ec, opacity: 0.065, speedX: 0.28, speedY: 0.46, rotSpeed: -0.018, phase: 3.2 },
      { x: 7.8, y: 1.8, z: 3.8, scale: 5.8, color: 0x38bdf8, opacity: 0.050, speedX: 0.36, speedY: 0.26, rotSpeed: 0.012, phase: 4.6 },
      { x: 6.8, y: -1.8, z: 3.2, scale: 5.0, color: 0xe06d44, opacity: 0.055, speedX: 0.32, speedY: 0.38, rotSpeed: -0.014, phase: 1.2 },
      { x: 5.6, y: 1.6, z: 2.6, scale: 4.8, color: 0xf5eadc, opacity: 0.060, speedX: 0.44, speedY: 0.34, rotSpeed: 0.016, phase: 2.7 },
      { x: 8.2, y: -0.2, z: 4.2, scale: 6.2, color: 0x2a2824, opacity: 0.080, speedX: 0.26, speedY: 0.24, rotSpeed: -0.011, phase: 4.9 },
    ];

    const allConfigs = [...leftPuffConfigs, ...rightPuffConfigs];

    allConfigs.forEach((cfg) => {
      const mat = new THREE.MeshBasicMaterial({
        map: mistTexture,
        color: cfg.color,
        transparent: true,
        opacity: cfg.opacity,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      });

      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(cfg.x, cfg.y, cfg.z);
      mesh.scale.set(cfg.scale, cfg.scale, 1);
      mesh.rotation.z = cfg.phase * Math.PI;

      mesh.userData = {
        baseX: cfg.x,
        baseY: cfg.y,
        baseZ: cfg.z,
        baseScale: cfg.scale,
        baseOpacity: cfg.opacity,
        speedX: cfg.speedX,
        speedY: cfg.speedY,
        rotSpeed: cfg.rotSpeed,
        phase: cfg.phase,
      };

      mistGroup.add(mesh);
      this.mistPuffs.push(mesh);
    });

    return mistGroup;
  }

  adjustForScreenSize(width) {
    if (width < 768) {
      // Mobile: 20% more compact scales and non-colliding positions
      this.basePositions.guitar.x = -3.2;
      this.basePositions.guitar.y = 3.2;
      this.basePositions.guitar.scale = 0.52;

      this.basePositions.keyboard.x = 3.2;
      this.basePositions.keyboard.y = 3.2;
      this.basePositions.keyboard.scale = 0.58;

      this.basePositions.headset.x = -2.8;
      this.basePositions.headset.y = -5.0;
      this.basePositions.headset.scale = 0.50;
    } else if (width < 1200) {
      // Tablet: 20% scaled down for editorial balance
      this.basePositions.guitar.x = -5.2;
      this.basePositions.guitar.y = 0.4;
      this.basePositions.guitar.scale = 0.70;

      this.basePositions.keyboard.x = 5.2;
      this.basePositions.keyboard.y = 0.2;
      this.basePositions.keyboard.scale = 0.76;

      this.basePositions.headset.x = -4.5;
      this.basePositions.headset.y = -4.2;
      this.basePositions.headset.scale = 0.64;
    } else {
      // Desktop: 20% reduced scales providing generous breathing room for typography
      this.basePositions.guitar.x = -6.6;
      this.basePositions.guitar.y = 0.4;
      this.basePositions.guitar.scale = 0.84;

      this.basePositions.keyboard.x = 6.4;
      this.basePositions.keyboard.y = 0.1;
      this.basePositions.keyboard.scale = 0.94;

      this.basePositions.headset.x = -5.8;
      this.basePositions.headset.y = -4.4;
      this.basePositions.headset.scale = 0.76;
    }
  }

  // Mouse leave handler
  onMouseLeave() {
    this.hasPointerMoved = false;
    this.activeHoveredModel = null;
    const keys = ['keyboard', 'headset', 'guitar'];
    for (const key of keys) {
      const m = this.modelInteractions[key];
      m.targetProximity = 0;
      m.targetTiltX = 0;
      m.targetTiltY = 0;
    }
    if (!this.isDragging) {
      document.body.style.cursor = '';
    }
  }

  // Mouse move handler
  onMouseMove(e) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouseX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouseY = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    this.mouseNDC.set(this.mouseX, this.mouseY);
    this.hasPointerMoved = true;

    if (this.isDragging && this.activeDraggedModel) {
      const m = this.modelInteractions[this.activeDraggedModel];
      if (m) {
        const dx = e.clientX - this.lastPointerX;
        const dy = e.clientY - this.lastPointerY;
        m.dragVelocityY = dx * 0.012;
        m.dragVelocityX = dy * 0.012;
        m.dragRotationY += m.dragVelocityY;
        m.dragRotationX += m.dragVelocityX;
        this.lastPointerX = e.clientX;
        this.lastPointerY = e.clientY;
      }
    }
  }

  onMouseDown(e) {
    if (this.activeHoveredModel) {
      this.isDragging = true;
      this.activeDraggedModel = this.activeHoveredModel;
      this.lastPointerX = e.clientX;
      this.lastPointerY = e.clientY;
      document.body.style.cursor = 'grabbing';
    }
  }

  onMouseUp() {
    this.isDragging = false;
    this.activeDraggedModel = null;
    if (this.activeHoveredModel) {
      document.body.style.cursor = 'grab';
    } else {
      document.body.style.cursor = '';
    }
  }

  onTouchStart(e) {
    if (e.touches.length === 1) {
      const rect = this.renderer.domElement.getBoundingClientRect();
      this.mouseX = ((e.touches[0].clientX - rect.left) / rect.width) * 2 - 1;
      this.mouseY = -(((e.touches[0].clientY - rect.top) / rect.height) * 2 - 1);
      this.mouseNDC.set(this.mouseX, this.mouseY);
      this.hasPointerMoved = true;

      this.updateHoverAndProximity();
      if (this.activeHoveredModel) {
        this.isDragging = true;
        this.activeDraggedModel = this.activeHoveredModel;
        this.lastPointerX = e.touches[0].clientX;
        this.lastPointerY = e.touches[0].clientY;
      }
    }
  }

  onTouchMove(e) {
    if (this.isDragging && this.activeDraggedModel && e.touches.length === 1) {
      const m = this.modelInteractions[this.activeDraggedModel];
      if (m) {
        const dx = e.touches[0].clientX - this.lastPointerX;
        const dy = e.touches[0].clientY - this.lastPointerY;
        m.dragVelocityY = dx * 0.012;
        m.dragVelocityX = dy * 0.012;
        m.dragRotationY += m.dragVelocityY;
        m.dragRotationX += m.dragVelocityX;
        this.lastPointerX = e.touches[0].clientX;
        this.lastPointerY = e.touches[0].clientY;
      }
    }
  }

  onTouchEnd() {
    this.isDragging = false;
    this.activeDraggedModel = null;
  }

  onScroll() {
    this.scrollY = window.scrollY || window.pageYOffset || 0;
  }

  onResize() {
    if (this.isDisposed) return;
    const nw = this.container.clientWidth || window.innerWidth;
    const nh = this.container.clientHeight || window.innerHeight;

    this.camera.aspect = nw / nh;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(nw, nh);
    this.adjustForScreenSize(nw);
  }

  updateHoverAndProximity() {
    if (!this.hasPointerMoved) return;

    this.raycaster.setFromCamera(this.mouseNDC, this.camera);
    const aspect = this.camera.aspect || 1.6;
    const tempV = new THREE.Vector3();

    const keys = ['keyboard', 'headset', 'guitar'];
    const directHits = {};
    const distances = {};
    const screenPositions = {};

    for (const key of keys) {
      const m = this.modelInteractions[key];
      if (!m || !m.group) continue;

      // 1. Check direct raycast intersection on meshes
      const intersects = this.raycaster.intersectObjects(m.group.children, true);
      directHits[key] = (intersects && intersects.length > 0);

      // 2. Project 3D world center to screen NDC coordinates
      m.group.getWorldPosition(tempV);
      tempV.project(this.camera);
      screenPositions[key] = { x: tempV.x, y: tempV.y };
      m.screenPos.set(tempV.x, tempV.y);

      // 3. Aspect-corrected screen distance
      const dx = (this.mouseX - tempV.x) * aspect;
      const dy = (this.mouseY - tempV.y);
      distances[key] = Math.hypot(dx, dy);
    }

    // Determine targeted model
    let winningKey = null;
    let maxProximity = 0;

    // Direct raycast hit has highest priority
    for (const key of keys) {
      if (directHits[key]) {
        winningKey = key;
        maxProximity = 1.0;
        break;
      }
    }

    // If no direct hit, check proximity within radius
    if (!winningKey) {
      let closestKey = null;
      let minDistance = Infinity;

      for (const key of keys) {
        const dist = distances[key];
        const radius = this.modelInteractions[key].influenceRadius;
        if (dist < radius && dist < minDistance) {
          minDistance = dist;
          closestKey = key;
        }
      }

      if (closestKey) {
        const radius = this.modelInteractions[closestKey].influenceRadius;
        const ratio = 1 - (minDistance / radius);
        // Smooth Hermite cubic interpolation
        const prox = ratio * ratio * (3 - 2 * ratio);
        if (prox > 0.08) {
          winningKey = closestKey;
          maxProximity = prox;
        }
      }
    }

    this.activeHoveredModel = winningKey;

    // Enforce strict isolation: ONLY the winning model receives interaction
    for (const key of keys) {
      const m = this.modelInteractions[key];
      if (key === winningKey) {
        m.targetProximity = maxProximity;
        const sPos = screenPositions[key];
        const relX = (this.mouseX - sPos.x) * aspect;
        const relY = (this.mouseY - sPos.y);

        // Smooth interactive pitch (X) and yaw (Y) tilt proportional to cursor offset
        m.targetTiltX = -Math.max(Math.min(relY * 0.75, 0.45), -0.45) * m.proximity;
        m.targetTiltY = Math.max(Math.min(relX * 0.75, 0.55), -0.55) * m.proximity;
      } else {
        m.targetProximity = 0;
        m.targetTiltX = 0;
        m.targetTiltY = 0;
      }
    }

    // Update cursor feedback
    if (!this.isDragging) {
      if (this.activeHoveredModel) {
        document.body.style.cursor = 'grab';
      } else {
        document.body.style.cursor = '';
      }
    }
  }

  animate() {
    if (this.isDisposed) return;
    this.frameId = requestAnimationFrame(this.animate);

    const elapsed = this.clock.getElapsedTime();

    // 1. Update proximity & isolated mouse tilt
    this.updateHoverAndProximity();

    const keys = ['keyboard', 'headset', 'guitar'];
    for (const key of keys) {
      const m = this.modelInteractions[key];
      m.proximity += (m.targetProximity - m.proximity) * 0.12;
      m.tiltX += (m.targetTiltX - m.tiltX) * 0.09;
      m.tiltY += (m.targetTiltY - m.tiltY) * 0.09;

      // Independent inertia damping for each model
      if (!this.isDragging || this.activeDraggedModel !== key) {
        m.dragVelocityX *= 0.92;
        m.dragVelocityY *= 0.92;
        m.dragRotationX += m.dragVelocityX;
        m.dragRotationY += m.dragVelocityY;
        m.dragRotationX *= 0.988;
        m.dragRotationY *= 0.988;
      }
    }

    // 2. Smooth Scroll Progress Calculation
    const windowH = window.innerHeight || 800;
    const rawScrollProgress = Math.min(Math.max(this.scrollY / windowH, 0), 2.5);
    this.scrollProgress += (rawScrollProgress - this.scrollProgress) * 0.06;

    // 3. Dynamic Isolated Transformations

    // === MECHANICAL WASD MACROPAD ===
    const kbBase = this.basePositions.keyboard;
    const kbM = this.modelInteractions.keyboard;
    this.keyboardGroup.position.x = kbBase.x + this.scrollProgress * 2.2;
    this.keyboardGroup.position.y = kbBase.y + Math.sin(elapsed * 1.1) * 0.3 - this.scrollProgress * 1.8;
    this.keyboardGroup.position.z = kbBase.z + Math.cos(elapsed * 0.9) * 0.2 - this.scrollProgress * 2.5;

    this.keyboardGroup.rotation.x = kbBase.rx + Math.sin(elapsed * 0.8) * 0.06 + this.scrollProgress * 0.85 + kbM.tiltX + kbM.dragRotationX;
    this.keyboardGroup.rotation.y = kbBase.ry + Math.cos(elapsed * 0.7) * 0.06 - this.scrollProgress * 0.65 + kbM.tiltY + kbM.dragRotationY;
    this.keyboardGroup.rotation.z = kbBase.rz + this.scrollProgress * 0.4 + kbM.tiltY * 0.2;
    const kbHoverScale = 1 + kbM.proximity * 0.04;
    this.keyboardGroup.scale.setScalar(kbBase.scale * Math.max(1 - this.scrollProgress * 0.25, 0.6) * kbHoverScale);

    // === HEADSET ===
    const hsBase = this.basePositions.headset;
    const hsM = this.modelInteractions.headset;
    this.headsetGroup.position.x = hsBase.x - this.scrollProgress * 1.5;
    this.headsetGroup.position.y = hsBase.y + Math.sin(elapsed * 1.3 + 1.2) * 0.32 + this.scrollProgress * 2.6;
    this.headsetGroup.position.z = hsBase.z + Math.sin(elapsed * 0.7) * 0.25 - this.scrollProgress * 1.8;

    this.headsetGroup.rotation.x = hsBase.rx + Math.sin(elapsed * 0.6) * 0.08 - this.scrollProgress * 0.5 + hsM.tiltX + hsM.dragRotationX;
    this.headsetGroup.rotation.y = hsBase.ry + Math.cos(elapsed * 0.8) * 0.1 + this.scrollProgress * 0.9 + hsM.tiltY + hsM.dragRotationY;
    this.headsetGroup.rotation.z = hsBase.rz - this.scrollProgress * 0.3 + hsM.tiltX * 0.15;
    const hsHoverScale = 1 + hsM.proximity * 0.04;
    this.headsetGroup.scale.setScalar(hsBase.scale * Math.max(1 - this.scrollProgress * 0.2, 0.65) * hsHoverScale);

    // === B.C. RICH WARLOCK CYBER-EDITORIAL GUITAR ===
    const gtBase = this.basePositions.guitar;
    const gtM = this.modelInteractions.guitar;
    this.guitarGroup.position.x = gtBase.x - this.scrollProgress * 2.0;
    this.guitarGroup.position.y = gtBase.y + Math.cos(elapsed * 0.95) * 0.35 - this.scrollProgress * 2.2;
    this.guitarGroup.position.z = gtBase.z + Math.cos(elapsed * 0.8) * 0.2 - this.scrollProgress * 3.0;

    this.guitarGroup.rotation.x = gtBase.rx + Math.sin(elapsed * 0.65) * 0.07 + this.scrollProgress * 0.45 + gtM.tiltX + gtM.dragRotationX;
    this.guitarGroup.rotation.y = gtBase.ry + Math.sin(elapsed * 0.6) * 0.08 + this.scrollProgress * 1.2 + gtM.tiltY + gtM.dragRotationY;
    this.guitarGroup.rotation.z = gtBase.rz + Math.cos(elapsed * 0.75) * 0.05 + this.scrollProgress * 0.35 + gtM.tiltY * 0.15;
    const gtHoverScale = 1 + gtM.proximity * 0.04;
    this.guitarGroup.scale.setScalar(gtBase.scale * Math.max(1 - this.scrollProgress * 0.22, 0.62) * gtHoverScale);

    // === PARTICLES ===
    this.particlesGroup.rotation.y = elapsed * 0.02;
    this.particlesGroup.rotation.x = elapsed * 0.01;
    this.particlesGroup.position.z = this.scrollProgress * 4.0;

    // === MIST DRIFT (In front of models, subtle ambient breath, center text strictly clear) ===
    if (this.mistPuffs && this.mistPuffs.length) {
      const dt = 0.016;
      this.mistPuffs.forEach((mesh) => {
        const u = mesh.userData;
        mesh.position.x = u.baseX + Math.sin(elapsed * u.speedX + u.phase) * 0.35;
        mesh.position.y = u.baseY + Math.cos(elapsed * u.speedY + u.phase) * 0.25;
        mesh.rotation.z += u.rotSpeed * dt;
        mesh.material.opacity = u.baseOpacity * (0.85 + 0.15 * Math.sin(elapsed * 0.6 + u.phase));
      });

      // Subtle mouse parallax on the mist layer
      if (this.mistGroup) {
        this.mistGroup.position.x += (this.mouseNDC.x * 0.35 - this.mistGroup.position.x) * 0.04;
        this.mistGroup.position.y += (this.mouseNDC.y * 0.25 - this.mistGroup.position.y) * 0.04;
        this.mistGroup.position.z = -this.scrollProgress * 2.0;
      }
    }

    // Render frame
    this.renderer.render(this.scene, this.camera);
  }

  destroy() {
    this.isDisposed = true;
    if (this.frameId) cancelAnimationFrame(this.frameId);
    this.mistPuffs = [];

    document.body.style.cursor = '';

    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('mouseleave', this.onMouseLeave);
    window.removeEventListener('touchstart', this.onTouchStart);
    window.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('touchend', this.onTouchEnd);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);

    // Dispose all meshes, geometries, and materials
    this.scene.traverse((obj) => {
      if (obj.isMesh || obj.isPoints) {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) {
            obj.material.forEach((m) => {
              if (m.map) m.map.dispose();
              m.dispose();
            });
          } else {
            if (obj.material.map) obj.material.map.dispose();
            obj.material.dispose();
          }
        }
      }
    });

    if (this.renderer && this.renderer.domElement) {
      this.renderer.domElement.remove();
      this.renderer.dispose();
    }
  }
}
